import React from 'react';
import PropTypes from 'prop-types';
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimes, FaTimesCircle } from 'react-icons/fa';

/**
 * Alert component for user notifications
 * Supports success, warning, error, and info variants
 */
const Alert = ({
  type = 'info',
  title,
  message,
  children,
  dismissible = false,
  onDismiss,
  className = '',
}) => {
  const typeConfig = {
    success: {
      icon: FaCheckCircle,
      bgClass: 'tw-bg-green-50 tw-border-green-200',
      textClass: 'tw-text-green-800',
      iconClass: 'tw-text-green-600',
    },
    error: {
      icon: FaTimesCircle,
      bgClass: 'tw-bg-red-50 tw-border-red-200',
      textClass: 'tw-text-red-800',
      iconClass: 'tw-text-red-600',
    },
    warning: {
      icon: FaExclamationTriangle,
      bgClass: 'tw-bg-yellow-50 tw-border-yellow-200',
      textClass: 'tw-text-yellow-800',
      iconClass: 'tw-text-yellow-600',
    },
    info: {
      icon: FaInfoCircle,
      bgClass: 'tw-bg-blue-50 tw-border-blue-200',
      textClass: 'tw-text-blue-800',
      iconClass: 'tw-text-blue-600',
    },
  };

  const config = typeConfig[type];
  const Icon = config.icon;

  return (
    <div
      className={`tw-border tw-rounded-md tw-p-4 tw-flex tw-gap-3 ${config.bgClass} ${className}`}
      role="alert"
    >
      <Icon className={`tw-flex-shrink-0 ${config.iconClass}`} size={20} />

      <div className="tw-flex-1">
        {title && (
          <h4 className={`tw-font-semibold tw-text-sm tw-mb-1 ${config.textClass}`}>
            {title}
          </h4>
        )}
        {children
          ? <p className={`tw-text-sm ${config.textClass} tw-mb-0 tw-leading-relaxed`}>{children}</p>
          : message && <p className={`tw-text-sm ${config.textClass}`}>{message}</p>
        }
      </div>

      {dismissible && (
        <button
          onClick={onDismiss}
          className={`tw-flex-shrink-0 tw-bg-transparent tw-border-0 tw-cursor-pointer tw-p-1 tw-rounded-sm hover:tw-opacity-70 tw-transition-opacity ${config.textClass}`}
          aria-label="Dismiss alert"
        >
          <FaTimes size={16} />
        </button>
      )}
    </div>
  );
};

Alert.propTypes = {
  type:        PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  title:       PropTypes.string,
  message:     PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children:    PropTypes.node,
  dismissible: PropTypes.bool,
  onDismiss:   PropTypes.func,
  className:   PropTypes.string,
};

export default Alert;
